/**
 * In-app purchases (feature 'store'): the server credits gold and diamonds for purchases the platform store confirms,
 * exactly once per purchase, and takes them back when the store reports a refund or a chargeback.
 *
 * The products and what they credit: ../monetization-catalog.ts (the ONE catalogue; prices live in Google Play
 * Console). Providers: 'google-play' when the server has Google Play credentials (a GooglePlayPurchases client,
 * src/server/node/google-play.ts); 'dev-test' with config.store.devTest (a development switch: a token 'devtest.<id>'
 * is a completed purchase without any store). 'app-store' is reserved for the iOS client. Without any provider the
 * catalogue is still served (providers: []): the shop shows the offers with the catalogue's list prices and nothing
 * can be bought (the skeleton of the shop, docs/economy.md).
 *
 * Flow of a purchase (client: the platform store interface of src/client/platform/store.ts):
 *  1. store.catalog: the products the player may buy and the obfuscated account id (a keyed hash of the player id,
 *     indexed in C.storeAccounts) that the client passes to the store with the purchase.
 *  2. The store's purchase flow on the device; the client gets a purchase token.
 *  3. player.command 'store.redeem' { provider, productId, purchaseToken }: under the player's profile lock the server
 *     asks the store (purchases.products.get) whether the token is a completed purchase of that product; a purchase
 *     whose obfuscated account id is another player's is refused. Then, under the purchase's own lock, the credit:
 *     the new game state, the purchase document (C.purchases, id = purchaseId = sha256 of provider + token), the order
 *     id index and the per-player counts of limited products are ONE atomic storage batch with the command's journal
 *     entry and the audit entry. The same purchase again answers 'already_credited' (nothing added), to another player
 *     'purchase_other_account'. A limited product bought beyond its limit (two devices at once) is still credited: the
 *     player paid (logged); store.catalog stops offering it.
 *  4. After the credit the server consumes the purchase (purchases.products.consume, which also acknowledges it:
 *     Google refunds purchases not acknowledged within three days); a failed consume is retried every
 *     config.store.consumeRetryMs. The client consumes it too (result.consume): whichever comes first wins.
 *
 * Without the client: Google's real-time developer notifications (RTDN, POST /store/google-play/rtdn, authenticated
 * by the node layer) reach handleNotification: a completed one-time purchase whose obfuscated account id is known is
 * credited to that account (profile.changed tells its game), and consumed; a voided purchase (refund, chargeback,
 * revoked) is reversed. The voided purchases API is polled every config.store.voidedPollMs as a safety net.
 *
 * Reversal (voidPurchase): the credited gold and diamonds are taken back as far as the balance allows (never below
 * zero; the rest is recorded as the shortfall in the purchase and the audit for the operator), the purchase becomes
 * 'voided' (a later redeem of it is refused). A voided purchase the server never saw is stored as a voided tombstone.
 *
 * Credit rules of the catalogue: the quantity of a multi-quantity purchase and the first-purchase multiplier
 * (FIRST_PURCHASE: the first diamonds pack of an account is credited twice, recorded in C.storePlayers), stimulators of
 * a bundle, the "Diamond magnate" award of the original (player.diam_magnet) for a purchase of at least
 * DIAM_MAGNET_MIN_DIAMONDS diamonds.
 *
 * Locks: the player's profile lock, then 'purchase:<purchaseId>', then 'storeplayer:<playerId>' (every writer of a
 * purchase document holds the first two, the order the command path takes them; store.catalog takes only the last).
 * Nothing here logs a purchase token.
 */
import { hmacSha256Hex } from '../../../shared/crypto/hmac';
import { sha256Hex } from '../../../shared/crypto/sha256';
import { type JsonObject, type JsonValue, isPlainObject } from '../../../shared/json';
import { BackendError } from '../../../shared/protocol/errors';
import { type PlayerId, type ProfileSummary, STORE_PROVIDERS, type StoreCatalogResult, type StoreProductInfo, type StoreProviderId, type StoreRedeemCommandResult } from '../../../shared/protocol/model';
import { RuleError, type StoredGameState, argsObject, decodeState, encodeState, int32, stringArg } from '../../../game/rules/index';
import { META_COLLECTION } from '../migrations';
import { DIAM_MAGNET_MIN_DIAMONDS, FIRST_PURCHASE, type OfferTrigger, PLAY_PRODUCT_ID_PATTERN, STORE_PRODUCTS, type StoreProductDef, firstPurchaseApplies, storeProduct } from '../monetization-catalog';
import { type StorageBatchOp, putOp } from '../storage';
import type { AuthService } from './auth';
import type { CommandService, ServiceOpContext, ServiceOpHandler, ServiceOpOutcome } from './commands';
import { C, type ServiceDeps } from './deps';
import type { MonetizationAudit } from './monetization-audit';
import type { ProfileService } from './profile';

/** What the store says about a purchase (Google Play: purchases.products.get). */
export interface PlayPurchase {
  productId: string;
  orderId: string | null;
  state: 'purchased' | 'canceled' | 'pending';
  consumed: boolean;
  acknowledged: boolean;
  /** A test purchase (license tester). */
  test: boolean;
  obfuscatedAccountId: string | null;
  purchaseTimeMs: number;
  quantity: number;
}

/** One voided purchase (Google Play: purchases.voidedpurchases.list). */
export interface PlayVoidedPurchase {
  purchaseToken: string;
  orderId: string | null;
  voidedTimeMs: number;
  /** 0 user, 1 developer, 2 Google. */
  voidedSource: number | null;
  /** 0 other, 1 remorse, 2 not received, 3 defective, 4 accidental, 5 fraud, 6 friendly fraud, 7 chargeback. */
  voidedReason: number | null;
}

/** The Google Play Developer API as the store needs it (src/server/node/google-play.ts; tests use a fake). */
export interface GooglePlayPurchases {
  readonly packageName: string;
  /** The purchase of `productId` with this token, or null when Google knows no such purchase. Throws StoreApiError. */
  getPurchase(productId: string, purchaseToken: string): Promise<PlayPurchase | null>;
  /** Consumes (and so acknowledges) a purchase; an already consumed one succeeds. Throws StoreApiError. */
  consume(productId: string, purchaseToken: string): Promise<void>;
  /** Voided purchases since `startTimeMs` (one page). Throws StoreApiError. */
  voidedPurchases(startTimeMs: number, pageToken: string | null): Promise<{ items: PlayVoidedPurchase[]; nextPageToken: string | null }>;
}

/** A failed call of the store's API; `transient`: worth retrying (network, 5xx, 429), else the request is wrong. */
export class StoreApiError extends Error {
  constructor(
    message: string,
    readonly transient: boolean,
    readonly status: number | null = null,
  ) {
    super(message);
    this.name = 'StoreApiError';
  }
}

/** Google's real-time developer notification (the decoded `message.data` of the Pub/Sub push). */
export interface DeveloperNotification {
  version?: string;
  packageName?: string;
  eventTimeMillis?: string | number;
  oneTimeProductNotification?: { version?: string; notificationType?: number; purchaseToken?: string; sku?: string };
  voidedPurchaseNotification?: { purchaseToken?: string; orderId?: string; productType?: number; refundType?: number };
  subscriptionNotification?: unknown;
  testNotification?: { version?: string };
}

/** ONE_TIME_PRODUCT_PURCHASED / ONE_TIME_PRODUCT_CANCELED of oneTimeProductNotification.notificationType. */
export const ONE_TIME_PURCHASED = 1;
export const ONE_TIME_CANCELED = 2;

/** A stored purchase (C.purchases, id = purchaseId). */
export interface PurchaseRecord {
  purchaseId: string;
  provider: StoreProviderId;
  /** Kept for the server-side consume and the reversal lookups; never logged or sent to a client. */
  purchaseToken: string;
  productId: string;
  orderId: string | null;
  /** The credited player (null: a voided tombstone, or 'unassigned'). */
  playerId: PlayerId | null;
  /** 'unassigned': the store reported it but no account could be named (the next redeem credits it). */
  status: 'credited' | 'voided' | 'unassigned';
  /** What was credited (the first-purchase multiplier and the quantity included). */
  gold: number;
  diam: number;
  items: Record<string, number>;
  multiplier: number;
  quantity: number;
  test: boolean;
  source: 'client' | 'notification' | 'poll';
  createdAt: number;
  creditedAt: number | null;
  /** The server-side consume: 'none' for providers without one. */
  consume: 'pending' | 'done' | 'failed' | 'none';
  consumeAttempts: number;
  consumedAt: number | null;
  voidedAt: number | null;
  /** What the reversal took back and what it could not (the balance was lower; items are not taken back). */
  clawback: { gold: number; diam: number; shortGold: number; shortDiam: number } | null;
}

/** The store facts of a player (C.storePlayers, id = playerId). */
export interface StorePlayerRecord {
  /** Purchases per product id. */
  counts: Record<string, number>;
  /** The first-purchase multiplier was used (FIRST_PURCHASE). */
  firstPurchaseUsed?: boolean;
  /** When each offer window of a product opened (epoch ms; index = offerWindows index). */
  offers?: Record<string, (number | null)[]>;
}

/** The obfuscated account id -> player index (C.storeAccounts, id = the tag). */
export interface StoreAccountRecord {
  playerId: PlayerId;
}

/** The voided purchases poll's position (meta/store_voided). */
interface VoidedCursor {
  lastVoidedMs: number;
}

const VOIDED_CURSOR_ID = 'store_voided';
/** Google keeps voided purchases for 30 days. */
const VOIDED_WINDOW_MS = 30 * 24 * 3600_000;
const MAX_VOIDED_PAGES = 20;
const MAX_CONSUME_ATTEMPTS = 30;
const MAX_BALANCE = 2_147_483_647;
const DEV_TEST_TOKEN = /^devtest\.[A-Za-z0-9_-]{8,64}$/;
const TOKEN_PATTERN = /^[\x21-\x7e]+$/;

export interface StoreServiceOptions {
  /** Google Play Developer API client; null = the provider 'google-play' is off. */
  googlePlay: GooglePlayPurchases | null;
  /** Key of the obfuscated account ids (derived from AUTH_SECRET; a constant in-process). */
  accountKey: string;
  products?: readonly StoreProductDef[];
}

export function purchaseIdOf(provider: StoreProviderId, purchaseToken: string): string {
  return `pu_${sha256Hex(`${provider}\n${purchaseToken}`).slice(0, 40)}`;
}

/**
 * The game state with `gold` / `diam` added (clamped to the int32 balance) or taken (never below zero), `items`
 * (stimulators) added, and the "Diamond magnate" award set when `magnet`. Returns the change actually made.
 */
export function withCurrency(gameState: StoredGameState, gold: number, diam: number, extra: { items?: Record<string, number>; magnet?: boolean } = {}): { state: StoredGameState; gold: number; diam: number } {
  const w = decodeState(gameState);
  const apply = (have: number, delta: number) => (delta >= 0 ? Math.min(MAX_BALANCE, have + delta) : Math.max(0, have + delta));
  const g0 = Number(w.player.gold) || 0;
  const d0 = Number(w.player.diam) || 0;
  w.player.gold = int32(apply(g0, gold));
  w.player.diam = int32(apply(d0, diam));
  const stims = w.player.stim_behave as Record<string, number>;
  for (const [id, n] of Object.entries(extra.items ?? {})) stims[id] = Math.min(MAX_BALANCE, (Number(stims[id]) || 0) + n);
  if (extra.magnet) (w.player as unknown as Record<string, unknown>).diam_magnet = 1;
  return { state: encodeState(w), gold: Number(w.player.gold) - g0, diam: Number(w.player.diam) - d0 };
}

/**
 * Whether the player won a battle (the first_win trigger of offer windows): a stage beyond the first opened (the
 * *Max fields start at 1, the next stage to play) or experience earned.
 */
function hasWon(s: ProfileSummary): boolean {
  return s.careerMax > 1 || s.missionsMax > 1 || s.survivalMax > 1 || s.bossMax > 1 || s.exp > 0;
}

function triggerMet(t: OfferTrigger, s: ProfileSummary): boolean {
  return t.kind === 'first_win' ? hasWon(s) : s.level >= t.level;
}

/** A credited Google Play purchase whose server-side consume is still to be done. */
function awaitsConsume(p: PurchaseRecord): boolean {
  return p.provider === 'google-play' && p.status === 'credited' && (p.consume === 'pending' || p.consume === 'failed') && p.consumeAttempts < MAX_CONSUME_ATTEMPTS;
}

export class StoreService {
  private readonly products: readonly StoreProductDef[];
  private readonly play: GooglePlayPurchases | null;
  private readonly accountKey: string;
  /** Credited purchases whose server-side consume is still to be done: purchaseId -> when to try. */
  private readonly consumeQueue = new Map<string, number>();
  private consumeQueueLoaded = false;
  private nextVoidedPollAt = 0;
  private busy: Promise<void> | null = null;

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly profiles: ProfileService,
    private readonly audit: MonetizationAudit,
    opts: StoreServiceOptions,
  ) {
    this.products = opts.products ?? STORE_PRODUCTS;
    this.play = opts.googlePlay;
    this.accountKey = opts.accountKey;
  }

  /** Providers whose purchases this server verifies. */
  providers(): StoreProviderId[] {
    const out: StoreProviderId[] = [];
    if (this.play) out.push('google-play');
    if (this.d.config.store.devTest) out.push('dev-test');
    return out;
  }

  get enabled(): boolean {
    return this.providers().length > 0;
  }

  /** The obfuscated account id of a player (48 hex characters: a keyed hash, stable per server secret). */
  accountTag(playerId: PlayerId): string {
    return hmacSha256Hex(this.accountKey, `fwp-store-account\n${playerId}`).slice(0, 48);
  }

  registerOps(commands: CommandService): void {
    commands.registerServiceOp('store.redeem', this.redeem);
  }

  // ---------------------------------------------------------------------------------------------- store.catalog

  async catalog(playerId: PlayerId): Promise<StoreCatalogResult> {
    const tag = this.accountTag(playerId);
    if (!(await this.d.storage.get<StoreAccountRecord>(C.storeAccounts, tag))) await this.d.storage.put(C.storeAccounts, tag, { playerId } satisfies StoreAccountRecord);
    // Without a provider the products are listed all the same, with `providers` empty: the shop shows its offers and
    // says that payment is not connected yet (the skeleton of the shop); store.redeem refuses every purchase.
    const summary = (await this.profiles.getRecord(playerId))?.profile.summary;
    const now = this.d.clock.now();
    // Offer windows open the first time the player is seen meeting their condition (stored once, under a lock).
    const player = await this.d.mutex.run(storePlayerLock(playerId), async () => {
      const cur = (await this.d.storage.get<StorePlayerRecord>(C.storePlayers, playerId)) ?? { counts: {} };
      if (!summary) return cur;
      let changed = false;
      const offers = { ...(cur.offers ?? {}) };
      for (const p of this.products) {
        if (!p.active || p.offerWindows.length === 0) continue;
        const opened = [...(offers[p.productId] ?? [])];
        p.offerWindows.forEach((w, i) => {
          if (opened[i] == null && triggerMet(w.trigger, summary)) {
            opened[i] = now;
            changed = true;
          }
        });
        offers[p.productId] = opened.map((x) => x ?? null);
      }
      if (!changed) return cur;
      const next: StorePlayerRecord = { ...cur, offers };
      await this.d.storage.put(C.storePlayers, playerId, next);
      return next;
    });
    const products: StoreProductInfo[] = [];
    for (const p of this.products) {
      if (!p.active) continue;
      let until: number | null = null;
      if (p.offerWindows.length > 0) {
        const opened = player.offers?.[p.productId] ?? [];
        for (let i = 0; i < p.offerWindows.length; i++) {
          const at = opened[i];
          if (at != null && now < at + p.offerWindows[i].hours * 3600_000) until = Math.max(until ?? 0, at + p.offerWindows[i].hours * 3600_000);
        }
        if (until === null) continue;
      }
      const purchased = player.counts[p.productId] ?? 0;
      const available = p.limitPerAccount === null || purchased < p.limitPerAccount;
      products.push({
        productId: p.productId,
        kind: p.kind,
        gold: p.gold,
        diam: p.diam,
        items: { ...p.items },
        multiplier: this.multiplierFor(p, player),
        limitPerAccount: p.limitPerAccount,
        purchased,
        available,
        shop: p.shop ? { ...p.shop } : null,
        label: p.label,
        availableUntil: until,
        listPrices: { ...p.listPrices },
        bonusPct: p.bonusPct,
      });
    }
    return { providers: this.providers(), products, obfuscatedAccountId: tag };
  }

  /** The multiplier the next purchase of `p` gets (the first purchase x2). */
  private multiplierFor(p: StoreProductDef, player: StorePlayerRecord | null): number {
    return firstPurchaseApplies(p.productId) && !player?.firstPurchaseUsed ? FIRST_PURCHASE.multiplier : 1;
  }

  // ---------------------------------------------------------------------------------------------- store.redeem

  private readonly redeem: ServiceOpHandler = async (ctx, args) => {
    const o = argsObject(args, ['provider', 'productId', 'purchaseToken', 'orderId']);
    const provider = stringArg(o, 'provider', { oneOf: STORE_PROVIDERS }) as StoreProviderId;
    const productId = stringArg(o, 'productId', { maxLength: 139, pattern: PLAY_PRODUCT_ID_PATTERN });
    const token = stringArg(o, 'purchaseToken', { maxLength: 4096, pattern: TOKEN_PATTERN });
    const clientOrderId = o.orderId === undefined ? null : stringArg(o, 'orderId', { maxLength: 128, pattern: TOKEN_PATTERN });
    if (!this.providers().includes(provider)) throw new RuleError('provider_disabled', { provider });
    const product = storeProduct(productId, this.products);
    if (!product) throw new RuleError('product_unknown', { productId });
    const purchaseId = purchaseIdOf(provider, token);
    const known = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
    if (known) {
      if (known.status === 'voided') throw new RuleError('purchase_voided', { purchaseId });
      if (known.status === 'credited') {
        if (known.playerId !== ctx.playerId) throw new RuleError('purchase_other_account', { purchaseId });
        if (known.productId !== productId) throw new RuleError('purchase_invalid', { problem: 'product_mismatch', purchaseId });
        return { result: this.redeemResult('already_credited', known) };
      }
    }
    const verified = await this.verify(provider, productId, token);
    if (verified.productId !== productId) throw new RuleError('purchase_invalid', { problem: 'product_mismatch', purchaseId });
    if (verified.state === 'canceled') throw new RuleError('purchase_invalid', { problem: 'canceled', purchaseId });
    if (verified.state === 'pending') throw new RuleError('purchase_pending', { purchaseId });
    if (verified.obfuscatedAccountId && verified.obfuscatedAccountId !== this.accountTag(ctx.playerId)) throw new RuleError('purchase_other_account', { purchaseId });
    const quantity = Math.max(1, Math.min(100, Math.floor(verified.quantity) || 1));
    const result: StoreRedeemCommandResult = {
      status: 'credited',
      purchaseId,
      productId,
      gold: 0,
      diam: 0,
      items: {},
      multiplier: 1,
      test: verified.test,
      consume: provider === 'google-play',
    };
    const out: ServiceOpOutcome = {
      result: result as unknown as JsonValue,
      commit: (write) =>
        this.d.mutex.run(purchaseLock(purchaseId), async () => {
          const doc = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
          if (doc?.status === 'voided') throw new BackendError('VALIDATION_FAILED', 'The purchase was refunded', { reason: 'purchase_voided', purchaseId });
          if (doc?.status === 'credited') {
            if (doc.playerId !== ctx.playerId) throw new BackendError('VALIDATION_FAILED', 'The purchase belongs to another account', { reason: 'purchase_other_account', purchaseId });
            Object.assign(result, this.redeemResult('already_credited', doc));
            await write([]);
            return;
          }
          await this.d.mutex.run(storePlayerLock(ctx.playerId), async () => {
            const now = ctx.nowMs;
            const plan = await this.creditPlan(ctx.playerId, product, quantity);
            ctx.setGameState(withCurrency(ctx.gameState, plan.gold, plan.diam, { items: plan.items, magnet: plan.diam >= DIAM_MAGNET_MIN_DIAMONDS }).state);
            Object.assign(result, { gold: plan.gold, diam: plan.diam, items: plan.items, multiplier: plan.multiplier });
            const rec: PurchaseRecord = {
              purchaseId,
              provider,
              purchaseToken: token,
              productId,
              orderId: verified.orderId ?? clientOrderId,
              playerId: ctx.playerId,
              status: 'credited',
              gold: plan.gold,
              diam: plan.diam,
              items: plan.items,
              multiplier: plan.multiplier,
              quantity,
              test: verified.test,
              source: 'client',
              createdAt: doc?.createdAt ?? now,
              creditedAt: now,
              consume: provider === 'google-play' ? (verified.consumed ? 'done' : 'pending') : 'none',
              consumeAttempts: 0,
              consumedAt: verified.consumed ? now : null,
              voidedAt: null,
              clawback: null,
            };
            const { entry, op } = this.audit.prepare({
              area: 'store',
              event: 'credit',
              playerId: ctx.playerId,
              provider,
              ok: true,
              details: { purchaseId, productId, orderId: rec.orderId, gold: rec.gold, diam: rec.diam, items: rec.items, multiplier: rec.multiplier, quantity, test: rec.test, source: 'client' },
            });
            await write([...this.purchaseWrites(rec), ...plan.ops, op]);
            this.audit.stored(entry);
            if (rec.consume === 'pending') this.consumeQueue.set(purchaseId, 0);
          });
        }),
      afterCommit: async () => {
        if (result.status === 'credited' && this.consumeQueue.has(purchaseId)) await this.consumeNow(purchaseId);
      },
    };
    return out;
  };

  private redeemResult(status: StoreRedeemCommandResult['status'], doc: PurchaseRecord): JsonValue {
    const r: StoreRedeemCommandResult = {
      status,
      purchaseId: doc.purchaseId,
      productId: doc.productId,
      gold: doc.gold,
      diam: doc.diam,
      items: { ...(doc.items ?? {}) },
      multiplier: doc.multiplier ?? 1,
      test: doc.test,
      consume: doc.provider === 'google-play',
    };
    return r as unknown as JsonValue;
  }

  /** The purchase document and its order id index. */
  private purchaseWrites(rec: PurchaseRecord): StorageBatchOp[] {
    const ops: StorageBatchOp[] = [putOp(C.purchases, rec.purchaseId, rec)];
    if (rec.orderId) ops.push(putOp(C.purchaseOrders, rec.orderId.slice(0, 200), { purchaseId: rec.purchaseId }));
    return ops;
  }

  /**
   * What a purchase of `product` credits to the player now (the quantity and the first-purchase multiplier applied)
   * and the write of the player's store record (counts, the multiplier used up). Call under storePlayerLock.
   */
  private async creditPlan(playerId: PlayerId, product: StoreProductDef, quantity: number): Promise<{ gold: number; diam: number; items: Record<string, number>; multiplier: number; ops: StorageBatchOp[] }> {
    const cur = (await this.d.storage.get<StorePlayerRecord>(C.storePlayers, playerId)) ?? { counts: {} };
    const multiplier = this.multiplierFor(product, cur);
    const n = quantity * multiplier;
    const items: Record<string, number> = {};
    for (const [id, c] of Object.entries(product.items)) items[id] = c * n;
    const counts = { ...cur.counts, [product.productId]: (cur.counts[product.productId] ?? 0) + 1 };
    if (product.limitPerAccount !== null && counts[product.productId] > product.limitPerAccount) {
      this.d.logger.warn('limited product bought beyond its limit (credited: the player paid)', { playerId, productId: product.productId });
    }
    const next: StorePlayerRecord = { ...cur, counts, ...(multiplier > 1 ? { firstPurchaseUsed: true } : {}) };
    return { gold: product.gold * n, diam: product.diam * n, items, multiplier, ops: [putOp(C.storePlayers, playerId, next)] };
  }

  /** Asks the provider about a purchase token (RuleError for a token it does not confirm, UNAVAILABLE when it cannot be asked). */
  private async verify(provider: StoreProviderId, productId: string, token: string): Promise<PlayPurchase> {
    if (provider === 'dev-test') {
      if (!DEV_TEST_TOKEN.test(token)) throw new RuleError('purchase_invalid', { problem: 'bad_token' });
      return { productId, orderId: `DEVTEST.${sha256Hex(token).slice(0, 16)}`, state: 'purchased', consumed: true, acknowledged: true, test: true, obfuscatedAccountId: null, purchaseTimeMs: this.d.clock.now(), quantity: 1 };
    }
    if (provider !== 'google-play' || !this.play) throw new RuleError('provider_disabled', { provider });
    let p: PlayPurchase | null;
    try {
      p = await this.play.getPurchase(productId, token);
    } catch (err) {
      if (err instanceof StoreApiError && !err.transient) throw new RuleError('purchase_invalid', { problem: 'bad_token' });
      this.d.logger.warn('store not reachable', { provider, err });
      throw new BackendError('UNAVAILABLE', 'The store could not be asked right now: retry later', { reason: 'store_unavailable' });
    }
    if (!p) throw new RuleError('purchase_invalid', { problem: 'not_found' });
    return p;
  }

  // ---------------------------------------------------------------------------------------------- consume

  /** Consumes a credited Google Play purchase (a failure is retried by tick). */
  async consumeNow(purchaseId: string): Promise<boolean> {
    const doc = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
    if (!doc || doc.provider !== 'google-play' || doc.status !== 'credited' || doc.consume === 'done' || !this.play) {
      this.consumeQueue.delete(purchaseId);
      return doc?.consume === 'done';
    }
    let ok = false;
    let error: string | null = null;
    try {
      await this.play.consume(doc.productId, doc.purchaseToken);
      ok = true;
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    }
    const now = this.d.clock.now();
    await this.d.mutex.run(purchaseLock(purchaseId), async () => {
      const cur = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
      if (!cur || cur.consume === 'done') return;
      const attempts = cur.consumeAttempts + 1;
      const next: PurchaseRecord = { ...cur, consume: ok ? 'done' : 'failed', consumeAttempts: attempts, consumedAt: ok ? now : cur.consumedAt };
      const { entry, op } = this.audit.prepare({ area: 'store', event: 'consume', playerId: cur.playerId, provider: cur.provider, ok, details: { purchaseId, productId: cur.productId, attempts, ...(error ? { error: error.slice(0, 200) } : {}) } });
      await this.d.storage.batch([putOp(C.purchases, purchaseId, next), op]);
      this.audit.stored(entry);
      if (ok || attempts >= MAX_CONSUME_ATTEMPTS) this.consumeQueue.delete(purchaseId);
      else this.consumeQueue.set(purchaseId, now + this.d.config.store.consumeRetryMs);
    });
    return ok;
  }

  // ---------------------------------------------------------------------------------------------- notifications

  /**
   * One real-time developer notification (already authenticated by the transport). Returns what was done (logged by
   * the caller). Throws StoreApiError (transient) when the store could not be asked: the push is then retried.
   */
  async handleNotification(n: DeveloperNotification): Promise<string> {
    if (!this.play) return 'ignored: no Google Play credentials';
    if (n.packageName !== undefined && n.packageName !== this.play.packageName) {
      await this.audit.record({ area: 'store', event: 'notification', playerId: null, provider: 'google-play', ok: false, details: { kind: 'other_package', packageName: String(n.packageName).slice(0, 100) } });
      return 'ignored: another package';
    }
    if (n.testNotification) {
      await this.audit.record({ area: 'store', event: 'notification', playerId: null, provider: 'google-play', ok: true, details: { kind: 'test' } });
      return 'test notification';
    }
    const v = n.voidedPurchaseNotification;
    if (v && typeof v.purchaseToken === 'string') {
      const r = await this.voidPurchase('google-play', v.purchaseToken, { orderId: typeof v.orderId === 'string' ? v.orderId : null, source: 'notification', refundType: v.refundType ?? null });
      return `voided purchase: ${r}`;
    }
    const o = n.oneTimeProductNotification;
    if (o && typeof o.purchaseToken === 'string' && typeof o.sku === 'string') {
      if (o.notificationType === ONE_TIME_PURCHASED) return `purchase: ${await this.creditFromStore(o.sku, o.purchaseToken, 'notification')}`;
      if (o.notificationType === ONE_TIME_CANCELED) {
        await this.audit.record({ area: 'store', event: 'notification', playerId: null, provider: 'google-play', ok: true, details: { kind: 'canceled', purchaseId: purchaseIdOf('google-play', o.purchaseToken), productId: o.sku.slice(0, 139) } });
        return 'pending purchase canceled';
      }
    }
    return 'ignored';
  }

  /**
   * A purchase the store reported by itself: credited to the account of its obfuscated account id (and consumed).
   * Returns 'credited', 'already', 'unassigned' (no account named: kept for the next redeem), 'pending', 'invalid'.
   */
  async creditFromStore(productId: string, token: string, source: 'notification' | 'poll'): Promise<string> {
    const provider: StoreProviderId = 'google-play';
    const purchaseId = purchaseIdOf(provider, token);
    const known = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
    if (known && known.status !== 'unassigned') return 'already';
    const product = storeProduct(productId, this.products);
    const p = await this.play!.getPurchase(productId, token);
    if (!p || p.state === 'canceled' || !product || p.productId !== productId) {
      await this.audit.record({ area: 'store', event: 'notification', playerId: null, provider, ok: false, details: { kind: 'purchase', purchaseId, productId: productId.slice(0, 139), problem: !product ? 'product_unknown' : !p ? 'not_found' : p.state === 'canceled' ? 'canceled' : 'product_mismatch' } });
      return 'invalid';
    }
    if (p.state === 'pending') return 'pending';
    const owner = p.obfuscatedAccountId ? await this.d.storage.get<StoreAccountRecord>(C.storeAccounts, p.obfuscatedAccountId) : null;
    const playerId = owner?.playerId ?? null;
    const quantity = Math.max(1, Math.min(100, Math.floor(p.quantity) || 1));
    const base: PurchaseRecord = {
      purchaseId,
      provider,
      purchaseToken: token,
      productId,
      orderId: p.orderId,
      playerId: null,
      status: 'unassigned',
      gold: 0,
      diam: 0,
      items: {},
      multiplier: 1,
      quantity,
      test: p.test,
      source,
      createdAt: this.d.clock.now(),
      creditedAt: null,
      consume: 'pending',
      consumeAttempts: 0,
      consumedAt: null,
      voidedAt: null,
      clawback: null,
    };
    const account = playerId ? await this.auth.getAccount(playerId) : null;
    const rec = account ? await this.profiles.getRecord(account.playerId) : null;
    if (!account || !rec?.profile.gameState) {
      await this.d.mutex.run(purchaseLock(purchaseId), async () => {
        if (await this.d.storage.get(C.purchases, purchaseId)) return;
        const { entry, op } = this.audit.prepare({ area: 'store', event: 'notification', playerId, provider, ok: true, details: { kind: 'purchase', purchaseId, productId, orderId: p.orderId, unassigned: true } });
        await this.d.storage.batch([putOp(C.purchases, purchaseId, base), op]);
        this.audit.stored(entry);
      });
      return 'unassigned';
    }
    let outcome = 'already';
    await this.profiles.withRecord(account.playerId, (prof, a) =>
      this.d.mutex.run(purchaseLock(purchaseId), async () => {
        const doc = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
        if (doc && doc.status !== 'unassigned') return;
        if (!prof.profile.gameState) return;
        const gameState = prof.profile.gameState as unknown as StoredGameState;
        await this.d.mutex.run(storePlayerLock(a.playerId), async () => {
          const now = this.d.clock.now();
          const plan = await this.creditPlan(a.playerId, product, quantity);
          const credited = withCurrency(gameState, plan.gold, plan.diam, { items: plan.items, magnet: plan.diam >= DIAM_MAGNET_MIN_DIAMONDS });
          const next: PurchaseRecord = {
            ...base,
            playerId: a.playerId,
            status: 'credited',
            gold: plan.gold,
            diam: plan.diam,
            items: plan.items,
            multiplier: plan.multiplier,
            createdAt: doc?.createdAt ?? now,
            creditedAt: now,
            consume: p.consumed ? 'done' : 'pending',
            consumedAt: p.consumed ? now : null,
          };
          const { entry, op } = this.audit.prepare({ area: 'store', event: 'credit', playerId: a.playerId, provider, ok: true, details: { purchaseId, productId, orderId: next.orderId, gold: next.gold, diam: next.diam, items: next.items, multiplier: next.multiplier, quantity, test: next.test, source } });
          await this.profiles.commitCanonical(prof, a, credited.state, now, null, [...this.purchaseWrites(next), ...plan.ops, op], { kind: 'purchase', ref: purchaseId });
          this.audit.stored(entry);
          if (next.consume === 'pending') this.consumeQueue.set(purchaseId, 0);
          outcome = 'credited';
        });
      }),
    );
    if (outcome === 'credited') await this.consumeNow(purchaseId);
    return outcome;
  }

  /**
   * Reverses a refunded / charged back / revoked purchase once. Returns 'reversed', 'already', 'tombstone' (never
   * seen: a later redeem is refused) or 'unassigned' (reported but never credited).
   */
  async voidPurchase(provider: StoreProviderId, token: string, info: { orderId: string | null; source: 'notification' | 'poll'; refundType?: number | null; voidedTimeMs?: number }): Promise<string> {
    const purchaseId = purchaseIdOf(provider, token);
    const doc = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
    const now = this.d.clock.now();
    const details: JsonObject = { purchaseId, orderId: info.orderId, source: info.source, ...(info.refundType != null ? { refundType: info.refundType } : {}) };
    if (doc?.status === 'voided') return 'already';
    const owner = doc?.status === 'credited' && doc.playerId ? await this.auth.getAccount(doc.playerId) : null;
    if (!doc || doc.status !== 'credited' || !owner) {
      let outcome = doc ? 'unassigned' : 'tombstone';
      await this.d.mutex.run(purchaseLock(purchaseId), async () => {
        const cur = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
        if (cur?.status === 'voided') {
          outcome = 'already';
          return;
        }
        const base: PurchaseRecord = cur ?? {
          purchaseId,
          provider,
          purchaseToken: token,
          productId: '',
          orderId: info.orderId,
          playerId: null,
          status: 'voided',
          gold: 0,
          diam: 0,
          items: {},
          multiplier: 1,
          quantity: 0,
          test: false,
          source: info.source,
          createdAt: now,
          creditedAt: null,
          consume: 'none',
          consumeAttempts: 0,
          consumedAt: null,
          voidedAt: now,
          clawback: null,
        };
        const next: PurchaseRecord = { ...base, status: 'voided', voidedAt: now };
        const { entry, op } = this.audit.prepare({ area: 'store', event: 'void', playerId: cur?.playerId ?? null, provider, ok: true, details: { ...details, outcome: cur ? (cur.playerId ? 'account_gone' : 'unassigned') : 'tombstone' } });
        await this.d.storage.batch([putOp(C.purchases, purchaseId, next), op]);
        this.audit.stored(entry);
        this.consumeQueue.delete(purchaseId);
      });
      return outcome;
    }
    let outcome = 'already';
    await this.profiles.withRecord(owner.playerId, (prof, a) =>
      this.d.mutex.run(purchaseLock(purchaseId), async () => {
        const cur = await this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
        if (!cur || cur.status !== 'credited') return;
        let taken = { gold: 0, diam: 0 };
        let state: StoredGameState | null = null;
        if (prof.profile.gameState) {
          const r = withCurrency(prof.profile.gameState as unknown as StoredGameState, -cur.gold, -cur.diam);
          state = r.state;
          taken = { gold: -r.gold, diam: -r.diam };
        }
        const clawback = { gold: taken.gold, diam: taken.diam, shortGold: cur.gold - taken.gold, shortDiam: cur.diam - taken.diam };
        const next: PurchaseRecord = { ...cur, status: 'voided', voidedAt: now, clawback };
        const { entry, op } = this.audit.prepare({ area: 'store', event: 'void', playerId: a.playerId, provider, ok: true, details: { ...details, productId: cur.productId, ...clawback } });
        const ops = [putOp(C.purchases, purchaseId, next), op];
        if (state) await this.profiles.commitCanonical(prof, a, state, now, null, ops, { kind: 'refund', ref: purchaseId });
        else await this.d.storage.batch(ops);
        this.audit.stored(entry);
        if (clawback.shortGold > 0 || clawback.shortDiam > 0) this.d.logger.warn('refunded purchase: the balance did not cover the whole reversal', { playerId: a.playerId, purchaseId, ...clawback });
        this.consumeQueue.delete(purchaseId);
        outcome = 'reversed';
      }),
    );
    return outcome;
  }

  // ---------------------------------------------------------------------------------------------- background

  /** The sweep (BackendCore.tick): server-side consume retries and the voided purchases poll; one job at a time. */
  tick(now: number): Promise<void> {
    if (this.busy || !this.play) return this.busy ?? Promise.resolve();
    this.busy = this.runJobs(now)
      .catch((err) => this.d.logger.error('store background job failed', { err }))
      .finally(() => {
        this.busy = null;
      });
    return this.busy;
  }

  /** An operator restored a damaged purchase (admin server.repairDocument): a consume it still awaits is queued. */
  repaired(p: PurchaseRecord): void {
    if (this.consumeQueueLoaded && p && awaitsConsume(p)) this.consumeQueue.set(p.purchaseId, 0);
  }

  private async runJobs(now: number): Promise<void> {
    if (!this.consumeQueueLoaded) {
      this.consumeQueueLoaded = true;
      for (const p of await this.d.storage.list<PurchaseRecord>(C.purchases)) if (awaitsConsume(p)) this.consumeQueue.set(p.purchaseId, 0);
    }
    for (const [id, at] of [...this.consumeQueue]) if (at <= now) await this.consumeNow(id);
    const every = this.d.config.store.voidedPollMs;
    if (every > 0 && now >= this.nextVoidedPollAt) {
      this.nextVoidedPollAt = now + every;
      await this.pollVoided(now);
    }
  }

  /** Fetches the voided purchases since the last poll and reverses them. Returns how many were new. */
  async pollVoided(now = this.d.clock.now()): Promise<number> {
    if (!this.play) return 0;
    const cursor = await this.d.storage.get<VoidedCursor>(META_COLLECTION, VOIDED_CURSOR_ID);
    // A minute of overlap: a purchase voided at the boundary is seen twice (reversals are idempotent), never missed.
    const start = Math.max(now - VOIDED_WINDOW_MS + 60_000, (cursor?.lastVoidedMs ?? 0) - 60_000);
    let last = cursor?.lastVoidedMs ?? 0;
    let page: string | null = null;
    let fresh = 0;
    for (let i = 0; i < MAX_VOIDED_PAGES; i++) {
      const r = await this.play.voidedPurchases(start, page);
      for (const v of r.items) {
        const out = await this.voidPurchase('google-play', v.purchaseToken, { orderId: v.orderId, source: 'poll', voidedTimeMs: v.voidedTimeMs });
        if (out !== 'already') fresh++;
        last = Math.max(last, v.voidedTimeMs);
      }
      page = r.nextPageToken;
      if (!page) break;
    }
    await this.d.storage.put(META_COLLECTION, VOIDED_CURSOR_ID, { lastVoidedMs: last } satisfies VoidedCursor);
    return fresh;
  }

  // ---------------------------------------------------------------------------------------------- account deletion

  /** The player's store documents that go with the account (purchases stay: financial records). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    const ops: StorageBatchOp[] = [];
    if (await this.d.storage.get(C.storePlayers, playerId)) ops.push({ op: 'delete', collection: C.storePlayers, id: playerId });
    const tag = this.accountTag(playerId);
    if (await this.d.storage.get(C.storeAccounts, tag)) ops.push({ op: 'delete', collection: C.storeAccounts, id: tag });
    return ops;
  }

  /** A purchase (operators, tests). */
  getPurchase(purchaseId: string): Promise<PurchaseRecord | null> {
    return this.d.storage.get<PurchaseRecord>(C.purchases, purchaseId);
  }
}

function purchaseLock(purchaseId: string): string {
  return `purchase:${purchaseId}`;
}

/** The lock of a player's store record (C.storePlayers); taken after the profile and purchase locks. */
function storePlayerLock(playerId: PlayerId): string {
  return `storeplayer:${playerId}`;
}

/** Whether a value looks like a developer notification (the node layer decodes the Pub/Sub message). */
export function isDeveloperNotification(v: unknown): v is DeveloperNotification {
  return isPlainObject(v) && (typeof v.packageName === 'string' || v.packageName === undefined);
}

export type { ServiceOpContext };
